<template>
  <div @change="validateSection">
    <transition-group name="fade" :duration="{enter: 200, leave: 150}">
      <header :key="1">
        <h3>Delivery</h3>
      </header>

      <div class="content" :key="2">
        <div class="form__row--x">
          <label>Delivery Date</label>
          <div class="form__row">
            <smd-date-input v-model="deliveryDate"
                            :style="highlightIfError('deliveryDate')"
                            :min-date="minDate"
                            :max-date="maxDate"
                            :disabled="isClose"/>
            <div v-if="$v.deliveryDate.$error" class="error-messages">
              <span v-if="!$v.deliveryDate.notFuture">Cannot be a future date</span>
              <span v-if="!$v.deliveryDate.notTooOld">Cannot be older than 2 years</span>
            </div>
          </div>
        </div>

        <div class="form__row--x">
          <label>Delivery AOG</label>

          <div class="form__row max-AOG">
            <smd-segment type="pill"
                         v-model="deliveryAOGType"
                         :items="$options.aogTypes()"
                         :disabled="isClose"/>
          </div>
          <div class="form__row max-AOG" v-if="showExactAOG">
            <div class="smd-input-label">
              Weeks
              <input type="number" min="0" v-model="deliveryWeeks"
                     :style="highlightIfError('deliveryWeeks')"
                     :disabled="isClose"/>
            </div>
            <div class="smd-input-label">
              Days
              <input type="number" min="0" v-model="deliveryDays"
                     :style="highlightIfError('deliveryDays')"
                     :disabled="isClose"/>
            </div>
          </div>
          <div class="form__row" v-else>
            <div class="form__row__item">
              <label>Estimated AOG:</label>
              <select v-model="deliveryAOG"
                      :style="highlightIfError('deliveryAOG')"
                      :disabled="isClose">
                <option v-for="mode in $options.aogRanges()"
                        :value="mode.id"
                        :key="mode.id">
                  {{ mode.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="form__row--x" >
          <label>Induced Labor</label>
          <smd-segment type="pill"
                       v-model="isInduced"
                       :items="$options.presenceChoices"
                       :disabled="isClose"/>
        </div>

        <div class="form__row--x" v-if="isInduced">
          <label>Indication for Induction of Labor</label>
          <select v-model="inducedIndication"
                  :style="highlightIfError('inducedIndication')"
                  :disabled="isClose">
            <option v-for="mode in $options.inducedIndicationChoices()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>

        <div class="form__row--x" v-if="isInduced">
          <label>Method for Induction of Labor</label>
          <select v-model="inducedMethod"
                  :style="highlightIfError('inducedMethod')"
                  :disabled="isClose">
            <option v-for="mode in $options.inducedMethodChoices()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>

        <div class="form__row--x" v-if="isInduced">
          <label>Problems arising from Induced Labor</label>
          <select v-model="inducedComplication"
                  :style="highlightIfError('inducedComplication')"
                  :disabled="isClose">
            <option v-for="mode in $options.inducedComplicationChoices()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>

        <div class="form__row--x">
          <label>Did patient undergo TOLAC?</label>
          <div :style="highlightIfError('isTOLAC')">
            <smd-segment type="pill"
                         v-model="isTOLAC"
                         :items="$options.booleanChoices"
                         :disabled="isClose"/>
          </div>
        </div>

        <pogs-delivery-manner  :is-tolac="isTOLAC"></pogs-delivery-manner>
      </div>
      <header :key="4">
        <h3>Neonatal</h3>
      </header>

      <div class="content" :key="5">
        <div class="form__row--x" v-if="willShowNumberOfInfants">
          <label>Number of Infants</label>
          <div class="form__row">
            <smd-segment type="pill"
                         v-model="computedInfantsNumber"
                         :items="$options.infantsMultiple"
                         :disabled="isClose"/>
            <input type="number"
                   :value="computedInfantsNumber"
                   @change="onInfantNumberChanged"
                   min="6"
                   max="20"
                   v-if="infantsNumber > 5"
                   :disabled="isClose">
            <div class="smd-spacer"></div>
          </div>
        </div>

        <div class="form__section" v-for="(infant, index) in filteredInfantNeonatal" :key="index">
          <pogs-neonatal-form :index="index" :infant="infant"></pogs-neonatal-form>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
  import withSectionValidation from '../../../mixins/with-section-validation'
  import validations from '../../../validations/pregnancy-section-validations'
  import withErrorValidation from '../../../mixins/with-error-validation-mixin';

  import PogsNeonatalForm from './pogs-form__item--pregnancy--infant';
  import PogsDeliveryManner from './pogs-form__item--pregnancy--deliverymanner';

  import {mapGetters, mapMutations, mapActions} from 'vuex'
  import {createHelpers} from 'vuex-map-fields'
  import {
    newArrayWithObject, fn_getNameWithMap, ObjectModelToArrayModel,
    smdSelectModelReducer, injectCategoryIdToConditions, categorizeAOG, injectDisabledProperty
  }
    from "../../../utilities/utilityOperators";

  import {
    GETTER_CASE_FORM_STATE_FIELD,
    SETTER_CASE_FORM_STATE_FIELD,
    GETTER_PREGNANCY_INDICATION,
    ACTION_UPDATE_PREGNANCY_INDICATION,
    MUTATE_REMOVE_PREGNANCY_INDICATION,
    ACTION_UPDATE_MORBIDITY,
    MUTATE_REMOVE_MORBIDITY,
  } from '../../../store/forms/case-form-state'

  import {
    getMapFn,
    REF_MATERNAL_FETAL_FORM_DELIVERY,
    REF_MATERNAL_FETAL_FORM_CONCOMITANT_PROCEDURES,
    REF_MATERNAL_FETAL_FORM_NEONATAL_CONDITIONS,
    REF_MATERNAL_FETAL_FORM_INDICATIONS_CS,
    REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS,
    REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM,
    REF_MATERNAL_FETAL_FORM_INDICATIONS_CS_OBJ,
    REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS_OBJ,
    REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM_OBJ,
    REF_MATERNAL_FETAL_FORM_PRIMARY_REPEAT,
    REF_MATERNAL_FETAL_FORM_INCISION,
    REF_MATERNAL_FETAL_FORM_MORBIDITIES,
    REF_MATERNAL_FETAL_FORM_MORBIDITIES_CONDITIONS_OBJ,
    REF_AOG_TYPES,
    REF_AOG_RANGES,
    REF_INDUCED_INDICATION,
    REF_INDUCED_METHOD,
    REF_INDUCED_COMPLICATION,
    REF_MATERNAL_FETAL_FORM_NEONATAL_INJURY,
    REF_NEONATAL_CAUSEOFDEATH_CONDITIONS,
    REF_NEONATAL_CAUSEOFDEATH,
    REF_MATERNAL_MORTALITY_ATECEDENT_CAUSE_ANTECEDENTS_OBJ
  } from '../../../store/modules/referencedata2'

  const {mapFields} = createHelpers({
    getterType: GETTER_CASE_FORM_STATE_FIELD,
    mutationType: SETTER_CASE_FORM_STATE_FIELD
  });

  export default {
    mixins: [withSectionValidation, withErrorValidation],
    components:{PogsNeonatalForm, PogsDeliveryManner},
    computed: {
      ...mapGetters({
        deliveryIndications: GETTER_PREGNANCY_INDICATION,

      }),
      ...mapFields([
        'isPregnancy',
        'isDelivered',
        'isTOLAC',

        'pregnancyType',
        'deliveryAOG',
        'deliveryAOGType',
        'deliveryDate',
        'deliveryWeeks',
        'deliveryDays',
        'deliveryManner',
        'primaryRepeat',

        // Added tagging of procedure
        'CSBilateralSalpingectomy',
        'CSBTL',
        //Induction of Labor
        'isInduced',
        'inducedIndication',
        'inducedMethod',
        'inducedComplication',
        'returnToOR',

        'incision',
        'infantsNumber',
        'deliveryIndication',
        'concomitantProcedure',
        'neonatal',
        'morbidities',
        'motherCondition',
        'deathReviewed',
        'isClose'
      ]),
      isCS() {
        let list = [1];
        return !!list.includes(this.deliveryManner)
      },
      isForceps() {
        let list = [5];
        return !!list.includes(this.deliveryManner)
      },
      isVacuum() {
        let list = [6];
        return !!list.includes(this.deliveryManner)
      },
      willShowNumberOfInfants() {
        return this.pregnancyType !== 1
      },
      willShowIndication() {
        return this.isCS || this.isForceps || this.isVacuum
      },
      computedPregnancyType: {
        get() {
          return this.pregnancyType
        },
        set(value) {
          this.pregnancyType = value;
          // Assume that 1 is for singleton pregnancy reference
          this.computedInfantsNumber = value === 1 ? 1 : 2
        }
      },
      computedInfantsNumber: {
        get() {
          return this.infantsNumber
        },
        set(newInfantCount) {
          this.infantsNumber = newInfantCount;
          console.log('Old Neonatal Array')
          console.log(this.neonatal)
          if (this.neonatal.length < newInfantCount) {
            let extraElements = newArrayWithObject(this.$options.infantObjectTemplate)(newInfantCount - this.neonatal.length);
            this.neonatal = this.neonatal.concat(extraElements)
          }
        }
      },
      filteredInfantNeonatal() {
        if (this.computedInfantsNumber == null)
          return [];
        else
          return this.neonatal.slice(0, this.computedInfantsNumber)
      },
      deliveryIndicationsLookup() {
        let mapFnName = null;

        if (this.isCS)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS;
        else if (this.isForceps)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS;
        else if (this.isVacuum)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM;

        return mapFnName ? injectDisabledProperty(getMapFn(mapFnName)()) : [];
      },
      deliveryIndicationsLookupObj() {
        let mapFnName = null;

        //TODO maybe a singular object for indication types regardless of  deliveryManner
        if (this.isCS)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS_OBJ;
        else if (this.isForceps)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS_OBJ;
        else if (this.isVacuum)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM_OBJ;

        return mapFnName ? getMapFn(mapFnName)() : {};
      },
      hasIndications() {
        return !!Object.keys(this.deliveryIndications || {}).length;
      },
      showMorbidities() {
        return !!Object.keys(this.morbidities || {}).length;
      },
      showExactAOG() {
        return this.deliveryAOGType === 1;
      },
      morbiditiesModel: {
        get() {
          return ObjectModelToArrayModel(this.morbidities, getMapFn(REF_MATERNAL_FETAL_FORM_MORBIDITIES_CONDITIONS_OBJ))
        },
        set(v) {
          const model = v.reduce(smdSelectModelReducer, {});
          this.doUpdateMorbiditiesModel(model)
        }
      },
      deliveryIndicationsModel: {
        get() {
          const map = this.deliveryIndicationsLookupObj;
          if(this.hasIndications && Object.keys(map).length > 0) {
            const keys = Object.keys(this.deliveryIndications);

            return keys.map((v) => ({
              id: map[v].id,
              name: fn_getNameWithMap(map)(v)
            }));
          }

          return [];
        },
        set(v) {
          let value = {};
          if(v instanceof Array && v.length) {
            value = v.reduce((acc, o) => {
              acc[o.id] = {id: o.id};
              return acc;
            }, {})
          }
          this.doUpdateMultiSelectModel(value);
        }
      },
      morbiditiesLookup() {
        return injectCategoryIdToConditions(getMapFn(REF_MATERNAL_FETAL_FORM_MORBIDITIES))()
      },
      minDate() {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2, 0, 1);
        return twoYearsAgo.toISOString().split('T')[0];
      },
      maxDate() {
        const today = new Date();
        return today.toISOString().split('T')[0];
      },
    },
    methods: {
      ...mapActions({
        doUpdateMultiSelectModel: ACTION_UPDATE_PREGNANCY_INDICATION,
        doUpdateMorbiditiesModel: ACTION_UPDATE_MORBIDITY
      }),
      ...mapMutations({
        removeIndication: MUTATE_REMOVE_PREGNANCY_INDICATION,
        removeMorbidity: MUTATE_REMOVE_MORBIDITY
      }),
      addConcomittant(e) {
        this.$emit('addConcomittant', e)
      },
      decodeIndication(id) {
        const o = this.deliveryIndicationsLookupObj[id];
        return o ? o.name : "";
      },
      onInfantNumberChanged(e) {
        this.computedInfantsNumber = e.target.value
      },
      checkForErrors(errors) {
        return Object.keys(this.$options.validations).reduce( (acc, key) => {
          if (key !== 'neonatal') {
            return acc || errors[key].$error;
          } else {
            let maxInfants = this.infantsNumber;
            let result = false;
            for(let i=0;i<maxInfants;i++) {
              result = result || errors.neonatal.$each[i].$error;
            }
            return acc || result;
          }
        }, false);
      },
      checkInfantMortality(status) {
        return [2,3,4,5,6].includes(status)
      },
      getNeonatalDeathSpecificCauses(causecategory) {
        if (causecategory === null)
          return [];
        else  {
          let result = getMapFn(REF_MATERNAL_MORTALITY_ATECEDENT_CAUSE_ANTECEDENTS_OBJ)()[causecategory]
          return result ? result.conditions : []
        }
      },
    },
    infantsMultiple: [
      {id: 2, name: 'Two'},
      {id: 3, name: 'Three'},
      {id: 4, name: 'Four'},
      {id: 5, name: 'Five'},
      {id: 6, name: 'Others'}
    ],
    congenitalAnomalies: [
      {id: 1, name: 'YES', val: true},
      {id: 2, name: 'NO', val: false}
    ],
    aogTypes: getMapFn(REF_AOG_TYPES),
    aogRanges: getMapFn(REF_AOG_RANGES),
    deliveryManners: getMapFn(REF_MATERNAL_FETAL_FORM_DELIVERY),
    concomitantProcedures: getMapFn(REF_MATERNAL_FETAL_FORM_CONCOMITANT_PROCEDURES),
    neonatalStatus: getMapFn(REF_MATERNAL_FETAL_FORM_NEONATAL_CONDITIONS),
    neonatalInjury: getMapFn(REF_MATERNAL_FETAL_FORM_NEONATAL_INJURY),
    neonatalCauseOfDeathCategory: getMapFn(REF_NEONATAL_CAUSEOFDEATH),
    neonatalCauseOfDeath: getMapFn(REF_NEONATAL_CAUSEOFDEATH_CONDITIONS),
    primaryOrRepeat: getMapFn(REF_MATERNAL_FETAL_FORM_PRIMARY_REPEAT),
    incisions: getMapFn(REF_MATERNAL_FETAL_FORM_INCISION),
    inducedIndicationChoices: getMapFn(REF_INDUCED_INDICATION),
    inducedMethodChoices: getMapFn(REF_INDUCED_METHOD),
    inducedComplicationChoices: getMapFn(REF_INDUCED_COMPLICATION),
    presenceChoices: [
      {id: 1, name: 'WITH', val: true},
      {id: 2, name: 'WITHOUT', val: false}
    ],
    booleanChoices: [
      {id: 1, name: 'YES', val: true},
      {id: 2, name: 'NO', val: false}
    ],
    infantObjectTemplate: {status: null, anomalies: null, pediatricAge: null, pediatricWeight:null, pediatricHeight:null, anomalyOthers: null, apgar1m: null, apgar5m: null, injury: null},
    validationSection: 'pregnancy',
    watch :{
      deliveryAOGType() {
        this.deliveryAOG = categorizeAOG(this.deliveryWeeks);
      },
      deliveryWeeks(val) {
        this.deliveryAOG = categorizeAOG(val);
      },
      deliveryManner(newVal, oldVal) {
        // Always reset the indications list once the mode of delivery changes
        if (newVal !== oldVal)
          this.deliveryIndicationsModel = [];
      },
      infantsNumber(val, oldval) {
        if (val !== oldval) {
          this.validateSection();
        }
      }
    },
    validations
  }
</script>

<style scoped lang="scss">
  @import "../../../scaffolding/scss/pogs-util";

  .max-AOG{
    input{
      max-width: 115px;
    }
  }
</style>
